import { Injectable } from '@angular/core';
import { BaseViewModel } from '../../../../../../../../models/base/base-view-model';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { VariantsGroupedByBrand } from '../../../../../../models/variants-grouped-by-brand';
import { map } from 'rxjs/operators';
import { GroupedByBrandShelfTalkerCardData } from '../../../../../../../../models/shelf-talkers/grouped-by-brand-shelf-talker-card-data';
import { SortUtils } from '../../../../../../../../utils/sort-utils';
import { PriceUtils } from '../../../../../../../../utils/price-utils';
import { LocationPriceStream } from '../../../../../../../../models/enum/shared/location-price-stream';
import { SectionUtils } from '../../../../../../../../utils/section-utils';
import { SectionColumnConfigFontStyle } from '../../../../../../../../models/enum/shared/section-column-config-font-style';

@Injectable()
export class ShelfTalkerPricingViewModel extends BaseViewModel {
  constructor() {
    super();
  }

  private _shelfTalkerCardData = new BehaviorSubject<GroupedByBrandShelfTalkerCardData>(null);
  public shelfTalkerCardData$ = this._shelfTalkerCardData as Observable<GroupedByBrandShelfTalkerCardData>;
  connectToShelfTalkerCardData = (shelfTalkerCardData: GroupedByBrandShelfTalkerCardData) => {
    this._shelfTalkerCardData.next(shelfTalkerCardData);
  };

  private _variantsGroupedByBrand = new BehaviorSubject<VariantsGroupedByBrand>(null);
  public variantsGroupedByBrand$ = this._variantsGroupedByBrand as Observable<VariantsGroupedByBrand>;
  connectToVariantsGroupedByBrand = (variantsGroupByBrand: VariantsGroupedByBrand) => {
    this._variantsGroupedByBrand.next(variantsGroupByBrand);
  };

  private _displayDecimalCharacter = new BehaviorSubject<boolean>(true);
  public displayDecimalCharacter$ = this._displayDecimalCharacter as Observable<boolean>;
  connectToDisplayDecimalCharacter = (displayDecimalCharacter: boolean) => {
    this._displayDecimalCharacter.next(displayDecimalCharacter);
  };

  private _priceStream = new BehaviorSubject<LocationPriceStream>(LocationPriceStream.Default);
  public priceStream$ = this._priceStream as Observable<LocationPriceStream>;
  connectToPriceStream = (priceStream: LocationPriceStream) => {
    this._priceStream.next(priceStream);
  };

  public prices$ = combineLatest([this.shelfTalkerCardData$, this.variantsGroupedByBrand$, this.priceStream$]).pipe(
    map(([shelfTalkerCardData, variantsGroupedByBrand, priceStream]) => {
      const themeId = shelfTalkerCardData?.menu?.theme;
      const locationId = shelfTalkerCardData?.locationConfig?.locationId;
      const companyId = shelfTalkerCardData?.companyConfig?.companyId;
      const hideSale = shelfTalkerCardData?.menu?.menuOptions?.hideSale;
      return variantsGroupedByBrand.variants
        ?.map(v => {
          return {
            price: v?.getVisiblePrice(themeId, locationId, companyId, priceStream, hideSale),
            onSale: v?.onSale(themeId, locationId, hideSale, priceStream)
          };
        })
        ?.uniqueByProperty('price', true)
        ?.sort((a, b) => SortUtils.numberAscending(a.price, b.price));
    })
  );

  public hasRange$ = this.prices$.pipe(map(prices => prices.length > 1));

  public hidePriceDecimal$ = this.shelfTalkerCardData$.pipe(
    map(shelfTalkerCardData => shelfTalkerCardData?.menu?.menuOptions?.hidePriceDecimal)
  );

  public minPriceOnSale$ = this.prices$.pipe(map(prices => prices?.firstOrNull()?.onSale));

  public minPrice$ = combineLatest([this.prices$, this.hidePriceDecimal$]).pipe(
    map(([prices, hidePriceDecimal]) => {
      return PriceUtils.formatPrice(prices?.firstOrNull()?.price, hidePriceDecimal);
    })
  );

  public minPriceInteger$ = this.minPrice$.pipe(map(minPrice => minPrice?.split('.')?.[0]?.replace('$', '')));

  public minPriceDecimal$ = combineLatest([this.minPrice$, this.displayDecimalCharacter$, this.hidePriceDecimal$]).pipe(
    map(([minPrice, displayDecimalCharacter, hidePriceDecimal]) => {
      return ShelfTalkerPricingViewModel.getPriceDecimal(minPrice, displayDecimalCharacter, hidePriceDecimal);
    })
  );

  public maxPriceOnSale$ = this.prices$.pipe(map(prices => prices?.last()?.onSale));

  public maxPrice$ = combineLatest([this.prices$, this.hidePriceDecimal$]).pipe(
    map(([prices, hidePriceDecimal]) => {
      return PriceUtils.formatPrice(prices?.last()?.price, hidePriceDecimal);
    })
  );

  public maxPriceInteger$ = this.maxPrice$.pipe(map(maxPrice => maxPrice?.split('.')?.[0]?.replace('$', '')));

  public maxPriceDecimal$ = combineLatest([this.maxPrice$, this.displayDecimalCharacter$, this.hidePriceDecimal$]).pipe(
    map(([maxPrice, displayDecimalCharacter, hidePriceDecimal]) => {
      return ShelfTalkerPricingViewModel.getPriceDecimal(maxPrice, displayDecimalCharacter, hidePriceDecimal);
    })
  );

  public locationConfig$ = this.variantsGroupedByBrand$.pipe(
    map(variantsGroupedByBrand => variantsGroupedByBrand?.locationConfig)
  );

  public salePriceFontStyle$ = this.locationConfig$.pipe(map(locationConfig => locationConfig?.salePriceFontStyle));

  public saleLabelFontColor$ = this.locationConfig$.pipe(map(locationConfig => locationConfig?.salePriceFontColor));

  public saleTextDecoration$ = this.salePriceFontStyle$.pipe(
    map(salePriceFontStyle => SectionUtils.getColumnTextDecoration(salePriceFontStyle))
  );

  public salePriceForceBold$ = this.salePriceFontStyle$.pipe(
    map(salePriceFontStyle => salePriceFontStyle === SectionColumnConfigFontStyle.Bold)
  );

  public salePriceForceItalic$ = this.salePriceFontStyle$.pipe(
    map(salePriceFontStyle => salePriceFontStyle === SectionColumnConfigFontStyle.Italics)
  );

  private static getPriceDecimal(price: string, displayDecimalCharacter: boolean, hidePriceDecimal: boolean): string {
    if (hidePriceDecimal) return '';
    const prefix = displayDecimalCharacter ? '.' : '';
    const decimalValue = price?.split('.')?.[1];
    return prefix + decimalValue;
  }
}
